import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';

const STORAGE_KEYS = {
  accessToken: 'auth.access_token',
  idToken: 'auth.id_token',
  refreshToken: 'auth.refresh_token',
  expiresAt: 'auth.expires_at',
  codeVerifier: 'auth.code_verifier',
  returnUrl: 'auth.return_url',
} as const;

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

@Injectable({providedIn: 'root'})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly clientId = environment.COGNITO_CLIENT_ID;
  private readonly authDomain = environment.COGNITO_AUTH_DOMAIN;
  private readonly redirectUri = environment.COGNITO_REDIRECT_URI;
  private readonly scopes = environment.COGNITO_SCOPES;

  isAuthenticated(): boolean {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);
    if (!expiresAt || !localStorage.getItem(STORAGE_KEYS.accessToken)) return false;
    return Date.now() < Number(expiresAt);
  }

  getAccessToken(): string | null {
    return this.isAuthenticated() ? localStorage.getItem(STORAGE_KEYS.accessToken) : null;
  }

  getIdToken(): string | null {
    return this.isAuthenticated() ? localStorage.getItem(STORAGE_KEYS.idToken) : null;
  }

  async getValidIdToken(): Promise<string | null> {
    if (!this.isAuthenticated()) {
      const refreshed = await this.tryRefreshTokens();
      console.log('Token refreshed:', refreshed);
      if (!refreshed) return null;
    }
    return localStorage.getItem(STORAGE_KEYS.idToken);
  }

  private async tryRefreshTokens(): Promise<boolean> {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!refreshToken) return false;

    try {
      const body = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('client_id', this.clientId)
        .set('refresh_token', refreshToken);

      const tokens = await firstValueFrom(
        this.http.post<Omit<TokenResponse, 'refresh_token'>>(`${this.authDomain}/oauth2/token`, body.toString(), {
          headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        }),
      );

      localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
      localStorage.setItem(STORAGE_KEYS.idToken, tokens.id_token);
      localStorage.setItem(STORAGE_KEYS.expiresAt, String(Date.now() + tokens.expires_in * 1000));
      return true;
    } catch {
      return false;
    }
  }

  async login(returnUrl = '/'): Promise<void> {
    sessionStorage.setItem(STORAGE_KEYS.returnUrl, returnUrl);
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scopes,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${this.authDomain}/login?${params}`;
  }

  async handleCallback(code: string): Promise<void> {
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.codeVerifier);
    if (!codeVerifier) throw new Error('No code verifier found — possible CSRF or stale tab');

    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', this.clientId)
      .set('redirect_uri', this.redirectUri)
      .set('code', code)
      .set('code_verifier', codeVerifier);

    const tokens = await firstValueFrom(
      this.http.post<TokenResponse>(`${this.authDomain}/oauth2/token`, body.toString(), {
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      }),
    );

    this.storeTokens(tokens);
    sessionStorage.removeItem(STORAGE_KEYS.codeVerifier);

    const returnUrl = sessionStorage.getItem(STORAGE_KEYS.returnUrl) ?? '/';
    sessionStorage.removeItem(STORAGE_KEYS.returnUrl);
    await this.router.navigateByUrl(returnUrl);
  }

  logout(): void {
    Object.values(STORAGE_KEYS).forEach(k => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });

    const params = new URLSearchParams({
      client_id: this.clientId,
      logout_uri: this.redirectUri,
    });

    window.location.href = `${this.authDomain}/logout?${params}`;
  }

  private storeTokens(tokens: TokenResponse): void {
    localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
    localStorage.setItem(STORAGE_KEYS.idToken, tokens.id_token);
    localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refresh_token);
    localStorage.setItem(STORAGE_KEYS.expiresAt, String(Date.now() + tokens.expires_in * 1000));
  }
}

function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', encoded);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
